import { ReactNode } from 'react';
import styled from 'styled-components';

import { type ScrollConfig } from 'components/insights/careTransitions/networkPerformance/slideout/headers/chartHelpers';
import CircularProgress from 'components/shared/CircularProgress';
import { colors } from 'styles/theme/colors';

import { TilesGrid } from './statTiles';

type Props = {
  tiles: ReactNode;
  chartTitle: string;
  chartLoading: boolean;
  showChart?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  scrollConfig?: ScrollConfig;
  children: ReactNode;
};

export default function MetricDetailLayout({
  tiles,
  chartTitle,
  chartLoading,
  showChart = true,
  isEmpty = false,
  emptyMessage = '\u2014',
  scrollConfig,
  children,
}: Props) {
  return (
    <Wrapper>
      <TilesGrid>{tiles}</TilesGrid>
      {showChart && (
        <ChartCard>
          <ChartTitle>{chartTitle}</ChartTitle>
          {chartLoading ? (
            <ChartPlaceholder>
              <CircularProgress color={colors.blue100} size={28} thickness={3} />
            </ChartPlaceholder>
          ) : isEmpty ? (
            <ChartPlaceholder>{emptyMessage}</ChartPlaceholder>
          ) : scrollConfig ? (
            <>
              <ScrollContainer>
                <ChartContent $minWidth={scrollConfig.minWidth}>{children}</ChartContent>
              </ScrollContainer>
              {scrollConfig.legend && (
                <ChartLegend>
                  <LegendDot $color={scrollConfig.legend.color} />
                  {scrollConfig.legend.label}
                </ChartLegend>
              )}
            </>
          ) : (
            children
          )}
        </ChartCard>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChartCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  padding: 20px 22px;
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    height: 0.5em;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.black50};
    border-radius: 5pt;
  }
`;

const ChartContent = styled.div<{ $minWidth: number }>`
  min-width: ${({ $minWidth }) => $minWidth}px;
`;

const ChartTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black100};
  margin-bottom: 16px;
`;

const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
  padding-top: 8px;
`;

const LegendDot = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ChartPlaceholder = styled.div`
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black25};
`;
